import { fetchClient } from "@/api/api";
import type {
	actOnPaymentInput,
	actOnPaymentOutput,
	createPaymentInput,
	createPaymentOutput,
	getAllPaymentsInput,
	getAllPaymentsOutput,
	getEurUsdRateOutput,
	getPaymentsFacetsOutput,
	getUserPaymentInformationInput,
	getUserPaymentInformationOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { createFacetSource } from "@/services/facets";
import { invalidateDebtQueries } from "@/services/invalidateDebt";
import { currentUserAtom } from "@/store/login";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { PAYMENT_SERVICE_KEYS } from "./keys";

const usePayments: CustomQueryHook<getAllPaymentsOutput, getAllPaymentsInput> =
	(payload, options) => {
		const user = useAtomValue(currentUserAtom);

		return useQuery({
			...options,
			queryKey: PAYMENT_SERVICE_KEYS.usePayments(payload),
			queryFn: async () => {
				const { data, error } = await fetchClient.GET(
					"/v1/marketplace/payments/",
					{
						params: {
							query: {
								...payload?.query,
								user_id: user?.id,
							},
						},
						throwOnError: true,
					},
				);
				if (error) throw error;
				return data;
			},
		});
	};

const useGetUserPaymentInfo: CustomQueryHook<
	getUserPaymentInformationOutput,
	getUserPaymentInformationInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PAYMENT_SERVICE_KEYS.useGetUserPaymentInfo(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/users/{user_id}/payment-info",
				{ params: { path: payload.path }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

/** EUR -> USD, so a EUR balance can be shown as the USDT amount to send. */
const useGetEurUsdRate = (enabled = true) =>
	useQuery({
		enabled,
		queryKey: PAYMENT_SERVICE_KEYS.useGetEurUsdRate(),
		// The server caches this for 30 minutes; don't re-ask on every dialog open.
		staleTime: 30 * 60 * 1000,
		queryFn: async (): Promise<getEurUsdRateOutput> => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/payments/eur-usd-rate/",
				{ throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});

const useCreatePayment: CustomMutationHook<
	createPaymentInput,
	createPaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: PAYMENT_SERVICE_KEYS.useCreatePayment(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/payments/",
				{
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},

		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PAYMENT_SERVICE_KEYS.usePayments(),
			});
			queryClient.invalidateQueries({
				queryKey: PAYMENT_SERVICE_KEYS.usePaymentsFacets(),
			});
			invalidateDebtQueries();
		},
	});
};

const useActOnPayment: CustomMutationHook<
	actOnPaymentInput,
	actOnPaymentOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: PAYMENT_SERVICE_KEYS.useActOnPayment(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/payments/{payment_id}",
				{
					params: { path: payload.path },
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},

		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PAYMENT_SERVICE_KEYS.usePayments(),
			});
			queryClient.invalidateQueries({
				queryKey: PAYMENT_SERVICE_KEYS.usePaymentsFacets(),
			});
			invalidateDebtQueries();
		},
	});
};

const PAYMENTS_FACET_SOURCE = createFacetSource<getPaymentsFacetsOutput>({
	path: "/v1/marketplace/payments/facets/",
	queryKey: (payload) => PAYMENT_SERVICE_KEYS.usePaymentsFacets(payload as any),
});

export {
	PAYMENTS_FACET_SOURCE,
	usePayments,
	useGetUserPaymentInfo,
	useGetEurUsdRate,
	useCreatePayment,
	useActOnPayment,
};
